import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
  Inventory as ReceiveIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface Shipment {
  id: number;
  shipment_number: string;
  from_location: string;
  expected_date: string;
  received_date?: string;
  status: 'pending' | 'in_transit' | 'received' | 'cancelled';
  total_items: number;
  total_expected_quantity: number;
  total_received_quantity: number;
  created_by_name: string;
  created_by_lastname: string;
  received_by_name?: string;
  received_by_lastname?: string;
  created_at: string;
}

const ShipmentList: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasPermission, hasRole } = useAuth();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shipmentToDelete, setShipmentToDelete] = useState<Shipment | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchShipments();
  }, [searchTerm, statusFilter]);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get(`/shipments?${params.toString()}`);
      if (response.data.success) {
        setShipments(response.data.data);
      }
    } catch (error: any) {
      setError('Failed to fetch shipments');
      console.error('Error fetching shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShipment = () => {
    navigate('/shipments/new');
  };

  const handleViewShipment = (id: number) => {
    navigate(`/shipments/${id}`);
  };

  const handleEditShipment = (id: number) => {
    navigate(`/shipments/${id}/edit`);
  };

  const handleDeleteShipment = (shipment: Shipment) => {
    setShipmentToDelete(shipment);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!shipmentToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`/shipments/${shipmentToDelete.id}`);
      await fetchShipments();
      setDeleteDialogOpen(false);
      setShipmentToDelete(null);
    } catch (error: any) {
      console.error('Error deleting shipment:', error);
      setError('Failed to delete shipment');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_transit':
        return 'info';
      case 'received':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <FilterIcon fontSize="small" />;
      case 'in_transit':
        return <ShippingIcon fontSize="small" />;
      case 'received':
        return <ReceiveIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const canEditShipment = (shipment: Shipment) => {
    return (hasPermission('shipments.update') || hasRole(['super_admin', 'admin'])) && shipment.status !== 'received';
  };

  const canDeleteShipment = (shipment: Shipment) => {
    return (hasPermission('shipments.delete') || hasRole(['super_admin', 'admin'])) && shipment.status !== 'received';
  };

  const canReceiveShipment = (shipment: Shipment) => {
    return (hasPermission('shipments.receive') || hasRole(['super_admin', 'admin'])) && shipment.status !== 'received';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Shipments
          </Typography>
          {(hasPermission('shipments.create') || hasRole(['super_admin', 'admin'])) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateShipment}
            >
              New Shipment
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box display="flex" gap={2} mb={3}>
          <TextField
            placeholder="Search shipments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_transit">In Transit</MenuItem>
              <MenuItem value="received">Received</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Shipments Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Shipment #</TableCell>
                <TableCell>From Location</TableCell>
                <TableCell>Expected Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell align="center">Expected Qty</TableCell>
                <TableCell align="center">Received Qty</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No shipments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                shipments.map((shipment) => (
                  <TableRow key={shipment.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {shipment.shipment_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{shipment.from_location}</TableCell>
                    <TableCell>
                      {shipment.expected_date ? format(new Date(shipment.expected_date), 'MMM dd, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const statusIcon = getStatusIcon(shipment.status);
                        const chipProps: any = {
                          label: shipment.status.replace('_', ' ').toUpperCase(),
                          color: getStatusColor(shipment.status),
                          size: "small" as const
                        };
                        if (statusIcon) {
                          chipProps.icon = statusIcon;
                        }
                        return <Chip {...chipProps} />;
                      })()}
                    </TableCell>
                    <TableCell align="center">{shipment.total_items}</TableCell>
                    <TableCell align="center">{shipment.total_expected_quantity}</TableCell>
                    <TableCell align="center">
                      {shipment.status === 'received' ? shipment.total_received_quantity : '-'}
                    </TableCell>
                    <TableCell>
                      {`${shipment.created_by_name} ${shipment.created_by_lastname}`}
                    </TableCell>
                    <TableCell>
                      {format(new Date(shipment.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewShipment(shipment.id)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {canEditShipment(shipment) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditShipment(shipment.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {canReceiveShipment(shipment) && (
                          <Tooltip title="Receive Shipment">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => navigate(`/shipments/${shipment.id}/receive`)}
                            >
                              <ReceiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {canDeleteShipment(shipment) && (
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteShipment(shipment)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Shipment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete shipment {shipmentToDelete?.shipment_number}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShipmentList;